'use client';

import React, { createContext, useRef, useLayoutEffect, useState, useEffect, useMemo } from 'react';
import AnnouncementBar from 'components/commercetools-ui/organisms/announcement-bar';
import Header from 'components/commercetools-ui/organisms/header';
import { HeaderProps, Market, Link } from 'components/commercetools-ui/organisms/header/types';
import { MarketProvider } from 'context/market';
import { useDebounce } from 'helpers/hooks/useDebounce';
import { useCart, useProduct } from 'frontastic/hooks';
import { TasticProps } from '../types';
import { useAnnouncementBarVisibility } from 'frontastic/hooks/useAnnouncementBarVisibility';
import { normalizeMenuItems } from './utils/menu-items';

const initialMarketState = {
  market: {} as Market,
  markets: [] as Market[],
  handleMarket: {} as (market: Market) => void,
};

// Links coming from frontastic
export interface NavigationMenuItem {
  label: string;
  link: Link;
  descendants?: NavigationMenuItem[];
}

export const MarketContext = createContext(initialMarketState);

const HeaderTastic = ({ data, categories }: TasticProps<HeaderProps>) => {
  const [query, setQuery] = useState('');
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);
  const { menuItems = [] } = data || {};
  const { barVisibility, handleBarClose } = useAnnouncementBarVisibility(data.announcementBars);

  const updateHeaderHeight = () => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  };

  useLayoutEffect(() => {
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  useEffect(() => {
    updateHeaderHeight();
  }, [barVisibility]);

  const debouncedQuery = useDebounce(query, 150);

  const { products, isProductSearchLoading } = useProduct({ query: debouncedQuery, limit: 6 });

  const categoryMenuItems = useMemo(
    () => normalizeMenuItems(categories?.filter((category) => category.depth === 0)),
    [categories],
  );
  const linkMenuItems = useMemo(() => normalizeMenuItems(menuItems), [menuItems]);
  const combinedMenuItems = [...categoryMenuItems, ...linkMenuItems];

  const {
    data: cart,
    isEmpty,
    totalItems: totalCartItems,
    removeItem,
    updateItem,
    redeemDiscountCode,
    removeDiscountCode,
  } = useCart();

  return (
    <MarketProvider>
      <div></div>
      <div id="header-container" ref={headerRef} className="fixed top-0 z-50 w-full">
        {data.announcementBars?.map((bar) => {
          const barId = bar.barId || 'default-announcement';
          const { onVisibilityChange: _, ...barProps } = bar;
          return (
            <AnnouncementBar
              key={`header-bar-${bar.text}`}
              {...barProps}
              barId={barId}
              visible={barVisibility[barId] || false}
              onClose={() => handleBarClose(barId)}
              onVisibilityChange={updateHeaderHeight}
            />
          );
        })}
        <Header
          cart={cart}
          isEmpty={isEmpty}
          onApplyDiscountCode={redeemDiscountCode}
          onRemoveDiscountCode={removeDiscountCode}
          onRemoveItem={removeItem}
          onUpdateItem={updateItem}
          totalCartItems={totalCartItems}
          menuItems={combinedMenuItems}
          categories={categories}
          logo={data.logo}
          logoLink={data.logoLink}
          logoMobile={data.logoMobile}
          logoLinkMobile={data.logoLinkMobile}
          tiles={data.tiles}
          searchItems={products}
          onSearchQueryUpdate={(q) => setQuery(q)}
          isProductSearchLoading={isProductSearchLoading}
          emptyCartTitle={data.emptyCartTitle}
          emptyCartSubtitle={data.emptyCartSubtitle}
          emptyCartCategories={data.emptyCartCategories}
          enableNavigationTitleButton={data.enableNavigationTitleButton}
          navigationTitleButtonLabel={data.navigationTitleButtonLabel}
          navigationTitleButtonLink={data.navigationTitleButtonLink}
          sideCartPaymentMethods={data.sideCartPaymentMethods}
        />
      </div>
      <div style={{ paddingTop: headerHeight }} className="transition-all"></div>
    </MarketProvider>
  );
};

export default HeaderTastic;
